// ChatState — the chat module's Model (split out of `app/chatDecider.ts`, C1).
//
// `sessions` keys per-conversation transcript/draft/drafting/profile (the
// existing `ChatSession`). `selectedConversationId` is the aggregate the
// composer and the chip act on; it survives presentation/navigation changes
// (the active tab is only a presentation fact) and was previously a sibling
// `useState` racing `frame.chat` — absorbed here (ADR-6).
// `liveness` (App.TurnLiveness — IDD 58538994) is the turn-lifecycle FACT this
// module no longer decides itself: "is a turn running (drafting) vs settled" per
// conversation + the stopped-turn fence, owned by a pure app-side reducer both
// shells project. `evolveChat` folds the liveness reducer alongside the
// transcript work and DERIVES `session.drafting` off it (so the terminal arm-set
// can't drift per-shell), and `decideChat` reads the fence (`isFenced`) to drop a
// late `text_delta`/reply belonging to a stopped or connection-lost turn. The
// per-session `drafting` boolean is now a PROJECTION of `liveness`, kept on the
// session for the many components that read it.
//
// Composer state (S4, chat-module-plan §2.1; design.md §1.5 Option B: read by
// several components and meaningful, so Model, not component-local):
//  - `composerMentions` — the draft's `@`-picked resources; spent by
//    `turn_started`.
//  - `pendingGrounding` — NASCENT conversations' pre-send grounding (F11),
//    flushed to durable Context on the first send.
// The local-entry id counter moved in too, per session (`ChatSession.
// nextLocalId`, chat/model.ts).

import * as TurnLiveness from "@bilda/app/turnLiveness";
import {
    emptyPendingGrounding,
    type PendingGrounding,
} from "../../app/pendingGrounding.js";
import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import type { ChatSession } from "./model.js";

export type ChatState = {
    readonly sessions: ReadonlyMap<string, ChatSession>;
    readonly selectedConversationId: string | null;
    readonly liveness: TurnLiveness.State;
    readonly composerMentions: readonly ComposerResourceRef[];
    readonly pendingGrounding: PendingGrounding;
};

export const initialChatState: ChatState = {
    sessions: new Map(),
    selectedConversationId: null,
    liveness: TurnLiveness.initial,
    composerMentions: [],
    pendingGrounding: emptyPendingGrounding,
};
